/* eslint-disable max-lines */
/* global window */

import { DYNAMIC_PANELS, INSPECTION_INFO_GROUP, GALLERY_NAVIGATION_TILES_DEFAULT_ORDER, INSPECTION_TAB, FILTER_INSPECTION_REPORT, WHATSAPP, GALLERY_IMAGE_MAX_HEIGHT_FOR_STICKY, GALLERY_IMAGE_MAX_WIDTH_FOR_STICKY, GALLERY_MINIMUM_NO_OF_IMAGE_FOR_STICKY } from 'Constants/items';
import { USER_TYPE } from 'Constants/users';
import { arrayToObject } from 'Helpers/array';
import { supportLocalStorage } from 'Helpers/localStorage';
import { isEmpty, omitUndefinedKeys } from 'Helpers/objects';
import keyBy from 'lodash/keyBy';
import { locationsKeyMapper } from 'Helpers/locations';
import { EXPONEA_EVENT_TO_KEY_MAP } from 'Constants/exponeaLeads';
import { fileNames } from 'Constants/imageUrls';
import { ONE_CRORE, ONE_LAKH, ONE_THOUSAND } from 'Constants/footerInterLinkData';
import { PRICE_INTERVAL_FOR_CATEGORY } from 'Constants/categories';
import { getUserInfoByCategory } from './users';
import { AD_IMPRESSION_DATA } from 'Constants/tracking';

const ROW_ITEM_COUNT = 3;
const YOUTUBE_VIDEO_ID_LENGTH = 11;
const YOUTUBE_LINK_REGEX = /(?:https?:\/\/)?(?:m\.)?(?:www\.)?youtu(?:\.be\/|(?:be-nocookie|be)\.com\/(?:watch|[\w]+\?(?:feature=[\w]+.[\w])?v=|v\/|e\/|embed\/|live\/|shorts\/|user\/(?:[\w#]+\/)+))([^&#?\n]+)/;
const GOOGLE_MAP_REGEX = /(https:\/\/(www\.)?google\.com\/maps\/(place|dir|search|embed|@|.*data=!.*)|https:\/\/(maps|goo)\.app\.goo\.gl\/[a-zA-Z0-9]+|https:\/\/goo\.gl\/maps\/[a-zA-Z0-9]+)/;

/**
 * Returns the total of selected levels to show from the location strinfigied.
 * @param {Array} levels - Levels available
 * @param {Number} levelsToShow - the first X levels that will be shown from location
 */
const getItemLocationString = (levels, levelsToShow) => {
    const selectedLevels = levels.slice(0, levelsToShow);
    const locationsArray = selectedLevels.map(elm => elm);

    return locationsArray.join(', ');
};

/**
 * Processes the mapping from the config and prioritizes
 * the order in which the locations will be shown
 * Order: NEIGHBOURHOOD -> CITY -> STATE -> COUNTRY
 * @param {Object} currentItem - current item being iterated
 * @param {Number} levelsToShow - number of selected levels
 */
export const getItemLocation = (currentItem, levelsToShow = 2) => {
    const locations = currentItem.locations_resolved;
    const possibleLevelsToShow = locations && locations.prioritizedLevels;

    if (!locations || !possibleLevelsToShow || !possibleLevelsToShow.length) {
        return '';
    }

    return getItemLocationString(possibleLevelsToShow, levelsToShow);
};

export const getItemSubsetData = item => {
    let returnParams = {};

    if (item) {
        returnParams = {
            adId: item.id,
            status: item.status.status,
            categoryId: item.category_id,
            imageCount: item.images.length,
            locations: {
                neighbourhoodId: item.locations[0].district_id,
                cityId: item.locations[0].city_id,
                stateId: item.locations[0].region_id,
                districtId: item.locations[0].district_id
            },
            autoBoostAtAdApplied: item.monetizationInfo.autoBoostAtAdApplied
        };
    }
    return returnParams;
};

export const getItemPanelsFromMetadata = item => {
    return item
        && item.metadata
        && item.metadata.category
        && item.metadata.category.panels || [];
};

export const isValidYoutubeLink = url => {
    // Regular expression for YouTube video URLs

    // Check if the URL matches the regex pattern
    return YOUTUBE_LINK_REGEX.test(url);
};

export const isValidGoogleMapLink = url => {
    // Regular expression for YouTube video URLs

    // Check if the URL matches the regex pattern
    return GOOGLE_MAP_REGEX.test(url);
};

export const getInspectionReportSections = damageReport => {
    return damageReport?.values
        && { ...damageReport,
            name: (damageReport.values).filter(({ id }) => id === INSPECTION_TAB.DENT_MAP)[0]?.name || '',
            values: (damageReport.values).filter(({ id }) => id === INSPECTION_TAB.DENT_MAP) || {}};
};

export const getTitles = (item, translations, config) => {
    const visiblePanels = getItemPanelsFromMetadata(item);
    const titles = [translations.overview];

    const irTitle = visiblePanels.includes(DYNAMIC_PANELS.INSPECTION_REPORT)
            && item.adAttributes
            && item.adAttributes.damageReport
            && (FILTER_INSPECTION_REPORT ? !isEmpty(getInspectionReportSections(item.adAttributes.damageReport)?.values) : !isEmpty((item.adAttributes.damageReport)?.values))
            && getInspectionReportSections(item.adAttributes.damageReport)?.name;

    titles.push(irTitle);

    let trTitle = '';

    const { adAttributes } = item;
    const technicalReport = adAttributes?.technicalReport;

    if (visiblePanels.includes(DYNAMIC_PANELS.TECHNICAL_REPORT) && technicalReport?.values?.length) {
        trTitle = technicalReport.name || '';
    }

    titles.push(trTitle);

    const fTitle = visiblePanels.includes(DYNAMIC_PANELS.FEATURES)
        && item.adAttributes
        && item.adAttributes.specialFeaturesSummary
        && item.adAttributes.specialFeaturesSummary.length > 0
        && translations.features;

    titles.push(fTitle);

    const cfWidgetTitle = visiblePanels.includes(DYNAMIC_PANELS.CONSUMER_FINANCING)
        && config.get('consumerFinance')?.adpv
        && translations.financing;

    titles.push(cfWidgetTitle);

    const swapWidgetTitle = visiblePanels.includes(DYNAMIC_PANELS.SWAP_CARS)
        && translations.exchange;

    titles.push(swapWidgetTitle);

    return titles;
};

const getAttributesFromParameters = (parameters = [], keys = {}) => {
    const parametersKeyBy = keyBy(parameters, 'key');
    const modelObj = parametersKeyBy[keys.model] ? parametersKeyBy[keys.model] : {};
    const yearObj = parametersKeyBy[keys.year] ? parametersKeyBy[keys.year] : {};
    const makeObj = parametersKeyBy[keys.make] ? parametersKeyBy[keys.make] : {};
    const variantObj = parametersKeyBy[keys.variant] ? parametersKeyBy[keys.variant] : {};

    return { makeObj, modelObj, variantObj, yearObj };
};

const removeModelFromVariant = (variant, model) => {
    if (variant) {
        const variantWithoutModel = variant.replace(model, '');

        return variantWithoutModel.trim();
    }
    return '';
};

export const getHiddenTitle = (item = {}, user = {}) => {
    const dealer = getUserInfoByCategory(user, item.category_id);
    const showroom_address = dealer?.showroom_address?.[0];
    const paramObj = arrayToObject(item.parameters || [], 'key');
    const make = paramObj.make?.formatted_value ? `${paramObj.make.formatted_value}` : '';
    const model = paramObj.model?.formatted_value ? ` ${paramObj.model.formatted_value}` : '';
    const engine = paramObj.engine?.formatted_value ? ` ${paramObj.engine.formatted_value}` : '';
    const variant = paramObj.variant?.formatted_value ? ` ${paramObj.variant.formatted_value}` : '';
    const fuel = paramObj.fuel?.formatted_value ? ` ${paramObj.fuel.formatted_value}` : '';
    const transmission = paramObj.transmission?.formatted_value ? ` ${paramObj.transmission.formatted_value}` : '';
    const mileage = paramObj.mileage?.formatted_value ? ` ${paramObj.mileage.formatted_value}` : '';
    const color = paramObj.color?.formatted_value ? ` ${paramObj.color.formatted_value}` : '';
    const city = showroom_address?.city ? ` ${showroom_address.city}` : '';

    return `${make}${model}${engine}${variant}${fuel}${transmission}${mileage}${color}${city}`;
};

export const getSeoTitle = (item = {}) => {
    const paramObj = arrayToObject(item.parameters || [], 'key');
    const make = paramObj.make?.formatted_value ? `used ${paramObj.make.formatted_value}` : '';
    const model = paramObj.model?.formatted_value ? ` ${paramObj.model.formatted_value}` : '';
    const type = paramObj.type?.formatted_value ? ` ${paramObj.type.formatted_value} ` : '';
    const job_type = paramObj.job_type?.formatted_value ? `${paramObj.job_type.formatted_value}` : '';
    const rooms = paramObj.rooms?.formatted_value ? `${paramObj.rooms.formatted_value} bhk` : '';
    const ft = paramObj.ft?.formatted_value ? ` ${paramObj.ft.formatted_value} sq-ft` : '';
    const yd = paramObj.yd?.formatted_value ? ` ${paramObj.yd.formatted_value} sq-yd` : '';

    return `${rooms}${type}${ft}${yd}${make}${model}${job_type}`;
};

export const getTitleAndHeadingFromInspection = (item, keys) => {
    const { adAttributes } = item;
    let { make, model, year, variant } = adAttributes || {};

    if (!make || !model || !variant || !year) {
        const { makeObj,
            modelObj,
            yearObj,
            variantObj } = getAttributesFromParameters(item.parameters, keys);

        make = make || makeObj.formatted_value || '';
        model = model || modelObj.formatted_value || '';
        year = year || yearObj.formatted_value || '';
        variant = variant || removeModelFromVariant(variantObj.formatted_value, model) || '';
    }
    const makeModelYear = `${make} ${model} (${year})`;
    const yearMakeModel = `${year} ${make} ${model}`;

    return { makeModelYear, variant, yearMakeModel };
};

export const createWhatsAppLink = (item, config, pathname, intl) => {
    const keys = config.get('adpvAuto', 'keys');
    const whatsAppNumber = config.get('adpvAuto', 'whatsAppNumber');
    const itemUrl = config.get('host') + pathname;

    const { makeModelYear, variant } = getTitleAndHeadingFromInspection(item, keys);
    const whatsAppMessage = `${intl?.formatMessage(WHATSAPP.MESSAGE)} (${makeModelYear} ${variant}) ${itemUrl}`;

    return `${WHATSAPP.LINK}phone=${whatsAppNumber}&text=${whatsAppMessage}`;
};

export const getDataByKey = (paramsArr, keyName) => {
    const data = keyBy(paramsArr, keyName);

    return data;
};

export const getUserTypeLowerCase = (user, categoryId) => {
    if (typeof user === 'string') {
        return user.toLowerCase();
    }
    const dealer = getUserInfoByCategory(user, categoryId);
    const dealerType = (dealer.dealer_type || '').toLowerCase();

    return dealerType;
};

export const isCocoUser = (user, categoryId) => getUserTypeLowerCase(user, categoryId) === USER_TYPE.COCO.toLowerCase();

export const isFofoUser = (user, categoryId) => getUserTypeLowerCase(user, categoryId) === USER_TYPE.FOFO.toLowerCase();

export const isCocoFofoUser = (user, categoryId) => isCocoUser(user, categoryId) || isFofoUser(user, categoryId);

/**
 * Deprecated Method - use isCocoFofoUser
 */
export const getIsCocoUser = user => {
    return user.dealer && isCocoFofoUser(user.dealer.car.dealer_type);
};

export const getIsStoreInspectedCar = inspection_info => inspection_info && inspection_info.inspection_id && inspection_info.group !== INSPECTION_INFO_GROUP.CONSUMER;

export const getIsSelfInspectedCar = inspection_info => inspection_info && inspection_info.inspection_id && inspection_info.group === INSPECTION_INFO_GROUP.CONSUMER;

export const getIsFeaturedAd = item => {
    if (!item) {
        return false;
    }

    const { 'package': appliedPackage, monetizationInfo } = item;
    const isAppliedPackage = appliedPackage && (appliedPackage.id || appliedPackage.name);
    const isFeaturedAdPackage = (!isEmpty(monetizationInfo) && monetizationInfo.currentProduct);

    return isAppliedPackage || isFeaturedAdPackage;
};

export const getExponeaItemProps = item => {
    if (!item) {
        return {};
    }

    const paramObj = arrayToObject(item.parameters || [], 'key');

    const itemObj = {
        adId: item.id,
        price: item.price && item.price.value && item.price.value.raw && item.price.value.raw.toString(),
        make: paramObj.make && paramObj.make.formatted_value,
        model: paramObj.model && paramObj.model.formatted_value,
        trim: paramObj.variant && paramObj.variant.formatted_value,
        year: paramObj.year && paramObj.year.formatted_value
    };

    return omitUndefinedKeys(itemObj);
};

export const getMappedParams = (ipObj = [], key, valueToMap = 'formatted_value') => {
    const paramObj = arrayToObject(ipObj || [], key);
    const mappedParams = {};

    for (const key in paramObj) {
        mappedParams[key] = paramObj[key] ? paramObj[key][valueToMap] : '';
    }

    return mappedParams;
};

export const getItemTrackingInfo = (item, user) => {
    if (!item) {
        return {};
    }
    const trackingInfo = {
        inspected_type: 'none'
    };
    const group = item.inspection_info && item.inspection_info.group;

    if (group === INSPECTION_INFO_GROUP.CONSUMER) {
        trackingInfo.inspected_type = 'self_inspection';
    }
    else if (group === INSPECTION_INFO_GROUP.INTERNAL) {
        trackingInfo.inspected_type = 'retail_inspection';
    }

    const userInfo = item.metadata
    && item.metadata.user && item.metadata.user[item.user_id] || user;

    const dealer = getUserInfoByCategory(userInfo, item.category_id);

    const userCategory = dealer.dealer_type;

    // In case of adpv user_category is mapped to userCategory, on listing and home page user_category is from item.user_type
    trackingInfo.user_category = userCategory || item.user_type || 'c2c';

    const { km_driven, m_year, make, year, model, mileage, m_tipe, fuel, petrol, variant, m_tipe_variant, m_fuel, transmission } = getMappedParams(item.parameters, 'key', 'formatted_value') || {};
    let i_km_driven; let i_m_year; let i_make; let i_year; let i_model; let i_mileage; let i_m_tipe; let i_fuel; let i_petrol; let i_variant; let i_m_tipe_variant; let i_m_fuel; let i_transmission;

    if (item.adAttributes && item.adAttributes.basicInfo && item.adAttributes.basicInfo.parameters) {
        ({ km_driven: i_km_driven, m_year: i_m_year, make: i_make, year: i_year, model: i_model, mileage: i_mileage, m_tipe: i_m_tipe, fuel: i_fuel, petrol: i_petrol, variant: i_variant, m_tipe_variant: i_m_tipe_variant, m_fuel: i_m_fuel, transmission: i_transmission } = getMappedParams(item.adAttributes.basicInfo.parameters, 'key', 'value') || {});
    }

    const price = item.price?.value?.raw || 0;

    trackingInfo.item_make = make || i_make || '';
    trackingInfo.item_model = model || m_tipe || i_model || i_m_tipe || '';
    trackingInfo.item_year = year || m_year || i_year || i_m_year || '';
    trackingInfo.item_variant = variant || m_tipe_variant || i_variant || i_m_tipe_variant || '';
    trackingInfo.item_mileage = mileage || km_driven || i_mileage || i_km_driven || '';
    trackingInfo.item_petrol = petrol || fuel || m_fuel || i_petrol || i_fuel || i_m_fuel || '';
    trackingInfo.item_fuel_type = fuel || m_fuel || i_fuel || i_m_fuel || '';
    trackingInfo.item_transmission = transmission || i_transmission || '';
    trackingInfo.item_price = price;

    return trackingInfo;
};

const levelValuesMapper = (locationsResolved, locationMapping) =>
    Object.keys(locationsResolved)
        .reduce((previousObj, currentElm) => {
            const SUFFIX = '_name';

            const containsSuffix = currentElm.indexOf(SUFFIX, currentElm.length - SUFFIX.length) !== -1;

            if (containsSuffix) {
                const key = currentElm.split(SUFFIX)[0];
                const value = locationsResolved[currentElm];
                const selectedKey = locationMapping ? locationsKeyMapper(key, locationMapping) : key;

                return {
                    ...previousObj,
                    [selectedKey]: value
                };
            }

            return previousObj;
        }, {});

export const getMappingPriortized = (elmLocationResolved, locationMapping) => {
    const mappedObject = levelValuesMapper(elmLocationResolved, locationMapping);
    const levelsValues = Object.keys(mappedObject).map(level => mappedObject[level]);

    levelsValues.reverse();

    return {
        ...mappedObject,
        prioritizedLevels: levelsValues
    };
};

export const getItemWithLocation = (item, config) => {
    const elmLocationResolved = item.locations_resolved;
    const locationMapping = config.get('locationMapping');

    if (!isEmpty(elmLocationResolved) && !elmLocationResolved.prioritizedLevels) {
        item.locations_resolved = getMappingPriortized(elmLocationResolved, locationMapping);
    }

    return item;
};

export const getItemCounts = (items = []) => {
    let totalFeaturedAds = 0;
    let totalInspectedAds = 0;

    items.forEach(item => {
        if (getIsStoreInspectedCar(item.inspection_info)) {
            totalInspectedAds++;
        }

        if (getIsFeaturedAd(item)) {
            totalFeaturedAds++;
        }
    });

    return {
        totalFeaturedAds,
        totalInspectedAds
    };
};

export const getItemVasDetail = item => (item && (item.vasTags || item.value_added_services)) || [];

export const getItemIndVasDetail = (items, index) => getItemVasDetail(items[index]);

export const getItemsVasDetail = (items, indexArr) => {
    let vasDetailArr = [];

    if (items) {
        vasDetailArr = indexArr.map(index => getItemIndVasDetail(items, index));
    }
    return vasDetailArr;
};

export const getMinPPInd = placeholderPosition => (placeholderPosition ? (placeholderPosition - (placeholderPosition % ROW_ITEM_COUNT)) : -1);

export const getMaxPPInd = minPPInd => (minPPInd >= 0 ? minPPInd + 2 : -1);

export const getIndDisp = (indDispArg, itemIndex, modPPInd) => {
    let toAdd = 0;
    let ppPassed = false;

    if (itemIndex + indDispArg >= modPPInd) {
        toAdd = 1;
        ppPassed = true;
    }
    const indDisp = indDispArg + (ROW_ITEM_COUNT - ((itemIndex + indDispArg + toAdd) % ROW_ITEM_COUNT));

    return [indDisp, ppPassed];
};

export const getIndModFromSection = (hasSection, sectionsOffset, itemIndex, indDispArg, modPPInd, placeholderPosition, minPPIndArg, maxPPIndArg) => {
    let nextSec;
    let indDisp = indDispArg;
    let ppPassed = false;
    let minPPInd = minPPIndArg;
    let maxPPInd = maxPPIndArg;

    if (hasSection) {
        const secIdInd = sectionsOffset.indexOf(itemIndex);

        nextSec = sectionsOffset[secIdInd + 1];
        const opArr = getIndDisp(indDisp, itemIndex, modPPInd);

        ([indDisp, ppPassed] = opArr);
        if (modPPInd && !ppPassed) {
            modPPInd = placeholderPosition + indDisp;
            minPPInd = getMinPPInd(modPPInd);
            maxPPInd = getMaxPPInd(minPPInd);
        }
    }
    return [nextSec, modPPInd, minPPInd, maxPPInd, indDisp];
};

export const getVasStripVisibilityForItemRow = (items, itemIndex, placeholderPosition, maxPPInd, minPPInd, showVasStripArg, nextSecInd, indDisp) => {
    let showVasStrip = showVasStripArg;
    const modItemInd = itemIndex + indDisp;
    let indicesToChk = [itemIndex, itemIndex + 1, itemIndex + 2]; // to chk 3 consecutive indicies
    let isFirstRowItem = !(modItemInd % ROW_ITEM_COUNT); // chk if its first item of the row or not
    let ind = 0;
    let maxInd = ROW_ITEM_COUNT;

    if (placeholderPosition) { // if place holder pos exists make modification based on index
        if (modItemInd >= maxPPInd) { // if ind is greater than maxPPInd increase indicies by one as pp item would be added
            isFirstRowItem = !((modItemInd + 1) % ROW_ITEM_COUNT);
        }
        else if (modItemInd === minPPInd) { // if Ind is equal to minPP we need to chk for two items only starting from this index as one pp will be there among them
            isFirstRowItem = true;
            maxInd = ROW_ITEM_COUNT - 1;
            indicesToChk = [itemIndex, itemIndex + 1];
        }
    }

    if (nextSecInd) {
        indicesToChk = [];
        while (ind < maxInd && itemIndex + ind < nextSecInd) {
            indicesToChk.push(itemIndex + ind);
            ind++;
        }
    }

    if (isFirstRowItem) {
        showVasStrip = false; // on every first row item reset it to false
        // CHK FOR other indicies
        let vasDetailArr = getItemsVasDetail(items, indicesToChk);

        vasDetailArr = vasDetailArr.filter(vasDetail => vasDetail && vasDetail.length);
        if (vasDetailArr && vasDetailArr.length) {
            showVasStrip = true;
        }
    }
    return showVasStrip;
};

export const getIsSpinCarEnabled = marketConfig => {
    const showSpinCar = marketConfig.get('adpvAuto', 'showSpinCar');

    return showSpinCar;
};

export const getGalleryNavTilesOrder = item => {
    const { adAttributes } = item || {};

    const { topGallerySequence } = adAttributes || {};
    let galleryNavTilesOrder = GALLERY_NAVIGATION_TILES_DEFAULT_ORDER;

    if (topGallerySequence && topGallerySequence.length > 0) {
        galleryNavTilesOrder = topGallerySequence.reduce((acc, { id: tileId }) => {
            acc.push(tileId);
            return acc;
        }, []);
    }
    return galleryNavTilesOrder;
};

export const getThankYouWidgetProps = (isMissedReserveOpened, isNotifyMe) => {
    if (isMissedReserveOpened) {
        return {
            title: 'youMissed',
            subTitle: 'someoneReserved',
            imgURL: fileNames.MISSED_RESERVED
        };
    }
    else if (isNotifyMe) {
        return {
            title: 'notifySuccessHeading',
            subTitle: 'notifySuccessSubtitle',
            imgURL: fileNames.THANKYOU_NOTIFY_SUCCESS
        };
    }
    return {
        title: 'thankYouVeryMuch',
        subTitle: 'interestedThankYouSubtitle',
        imgURL: ''
    };
};

export const clearLeadsDataFromStorage = () => {
    if (supportLocalStorage()) {
        window.sessionStorage.removeItem(EXPONEA_EVENT_TO_KEY_MAP.b2c_carSubmitLead);
    }
};

export const getYouTubeVideoId = url => {
    // Regular expression to match YouTube video ID in standard and Shorts URLs
    const match = url.match(YOUTUBE_LINK_REGEX);

    if (match && match[1]?.length === YOUTUBE_VIDEO_ID_LENGTH) {
        return match[1];
    }

    // Handle invalid YouTube URLs
    return null;
};

// function to check if pinToTop is applied for today
export const isPinToTopAppliedForToday = ad => {
    if (!ad?.pinToTopAtAdApplied || !ad?.featureInfo?.applicableDate) {
        return false;
    }

    const applicableDate = new Date(ad.featureInfo.applicableDate);
    const currentDate = new Date();

    applicableDate.setUTCHours(0, 0, 0, 0);
    currentDate.setUTCHours(0, 0, 0, 0);

    return applicableDate.getTime() === currentDate.getTime();
};

export const getIsSponsoredAd = item => {
    if (!item) {
        return false;
    }

    const pttPackageId = item.ptt_package_id;
    const itemSpellKey = item.spell?.key;
    const pinToTopAtAdApplied = isPinToTopAppliedForToday(item.monetizationInfo);

    const isPtt = (pttPackageId !== null && itemSpellKey === 'PIN_QUEST_ES') || pinToTopAtAdApplied;

    return isPtt;
};

export function formatPrice(raw, locale = undefined) {
    const min = Math.sign(raw.min) === -1 ? raw.min : Math.abs(raw.min);
    const max = Math.abs(raw.max);
    const ceil = (val, range) => val / range;

    if (min <= 0) {
        if (max >= ONE_LAKH) {
            return locale ? `${ceil(max, ONE_LAKH).toLocaleString(locale)} ${max > ONE_LAKH ? 'Lakhs' : 'Lakh'}` : `${ceil(max, ONE_LAKH)} ${max > ONE_LAKH ? 'Lakhs' : 'Lakh'}`;
        }
        if (max >= ONE_THOUSAND) {
            return locale ? `₹ ${max.toLocaleString(locale)}` : `${ceil(max, ONE_THOUSAND)} K`;
        }

        return max;
    }
    if (min >= ONE_CRORE && max >= ONE_CRORE) {
        return `${ceil(max, ONE_CRORE)} ${max > ONE_CRORE ? 'Crores' : 'Crore'}`;
    }
    if (min >= ONE_LAKH && max >= ONE_CRORE) {
        return `${ceil(max, ONE_CRORE)} ${max > ONE_CRORE ? 'Crores' : 'Crore'}`;
    }
    if (min >= ONE_THOUSAND && max >= ONE_LAKH) {
        return `${ceil(max, ONE_LAKH)} ${max > ONE_LAKH ? 'Lakhs' : 'Lakh'}`;
    }
    if (min >= ONE_THOUSAND && max >= ONE_THOUSAND) {
        return `₹ ${max.toLocaleString(locale)}`;
    }
    if (min <= ONE_LAKH && max >= ONE_LAKH) {
        return `${ceil(max, ONE_LAKH)} ${max > ONE_LAKH ? 'Lakhs' : 'Lakh'}`;
    }
    if (min <= ONE_THOUSAND && max >= ONE_THOUSAND) {
        return `₹ ${max.toLocaleString(locale)}`;
    }

    return `₹ ${max.toLocaleString(locale)}`;
}

export function getPriceRange(amount, categoryId) {
    let interval = PRICE_INTERVAL_FOR_CATEGORY[categoryId];

    if (amount <= interval) {
        return { interval, max: interval, min: 0 };
    }

    // (tanmay-mazumdar) Maybe we wont need this check.
    // eslint-disable-next-line no-magic-numbers
    if (interval / amount > 100) {
        // eslint-disable-next-line no-magic-numbers
        interval /= 100;
    }
    // eslint-disable-next-line no-magic-numbers
    else if (interval / amount > 10) {
        // eslint-disable-next-line no-magic-numbers
        interval /= 10;
    }

    const frac = amount / interval;
    let min;
    let max;

    if (Number.isInteger(frac)) { // integer or non frational value
        // eslint-disable-next-line no-magic-numbers
        min = (frac - 1) * interval;
        // eslint-disable-next-line no-magic-numbers
        max = frac * interval;
    }
    else {
        min = Math.floor(frac) * interval;
        max = Math.ceil(frac) * interval;
    }

    return { interval, min, max };
}

export const getItemLocationFromMeta = (item = {}, level = 4) => { // eslint-disable-line no-magic-numbers
    const [locality, city, state, country] = item.metadata?.locations[0]?.tree || [];
    const locations = [locality?.name || '', city?.name || '', state?.name || '', country?.name || ''];

    return locations.every(item => item === '') ? '' : locations.splice(0, level).join(' ');
};

export const getCategoryIdFromSlug = slug => {
    if (!slug) {
        return null;
    }
    const match = slug.match(/-c(\d+)/);

    return match ? match[1] : null;
};
// eslint-disable-next-line no-magic-numbers
export const getItemLocationForInterLinks = (item = {}, level = 4) => {
    const [country, state, city, locality] = [...(item?.metadata?.locations[0]?.tree || [])].reverse();

    return [country, state, city, locality][level - 1];
};

export const getFeaturedIntegrationTag = tags => {
    if (!tags) {
        return null;
    }

    return tags.package_type === 'FeaturedAd' && tags.tag ? tags : null;
};

export const showAdsOnGallery = imageArray => {
    if (imageArray.length < GALLERY_MINIMUM_NO_OF_IMAGE_FOR_STICKY) {
        return false;
    }
    return imageArray.every(image => image.height <= GALLERY_IMAGE_MAX_HEIGHT_FOR_STICKY && image.width <= GALLERY_IMAGE_MAX_WIDTH_FOR_STICKY);
};

export const isFranchiseView = (config = {}, categoryId) => {
    if (!categoryId) {
        return false;
    }

    const { enable_for_all = false, enabled_category = [] } = config?.franchise_view || {};

    if (!enable_for_all && Array.isArray(enabled_category)) {
        return enabled_category.includes(String(categoryId));
    }

    return enable_for_all;
};

export const showHomepageBanner = (carouselHomePage = {}, selectedLocation = {}, widgetKey = '') => {
    const { enabled, widgetName, city_id } = carouselHomePage || {};

    if (!enabled || !widgetName?.[widgetKey]) {
        return false;
    }
    if (city_id?.length === 0) {
        return true;
    }

    if (city_id?.length > 0) {
        return (carouselHomePage?.city_id?.includes(String(selectedLocation?.id)) || carouselHomePage?.city_id?.includes(String(selectedLocation?.parentId)));
    }

    return false;
};

export const getItemParameters = (item = {}) => {
    const mappedParams = {};

    if (item?.parameters?.length > 0) {
        for (const param of item.parameters) {
            if (param.key && param.formatted_value) {
                mappedParams[param.key] = param.formatted_value;
            }
        }
    }

    return mappedParams;
};

const trackEvent = (seenItemsRef, track) => {
    const ads = Object.values(seenItemsRef.current).map(item => ({
        ad_id: item.id,
        feature: !!getIsFeaturedAd(item),
        ptt: !!item.ptt_package_id,
        btt: !!item.btt_package_id
    }));

    track(AD_IMPRESSION_DATA, { impressions: ads });
};

export const handleItemSeen = (item, seenItemsRef, flag, track) => {
    if (!seenItemsRef.current[item.id] && flag) {
        seenItemsRef.current[item.id] = item;
        trackEvent(seenItemsRef, track);
    }
};
